/**
 * AppLoader - Full-screen interactive loader for app-level transitions
 * Use during: create organization, navigating to workspace, loading workspace after login
 * Wraps ContentLoader in a fixed overlay (same feel as PageLoader but with messages/icons)
 */

import { cn } from '@/lib/utils'
import { ContentLoader, type ContentLoaderProps } from './ContentLoader'

export interface AppLoaderProps extends ContentLoaderProps {
  /** Optional wrapper className (e.g. for background) */
  overlayClassName?: string
}

export function AppLoader({
  overlayClassName,
  minHeight = 'min-h-screen',
  size = 'lg',
  className,
  ...contentLoaderProps
}: AppLoaderProps) {
  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm',
        overlayClassName
      )}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <ContentLoader
        {...contentLoaderProps}
        minHeight={minHeight}
        size={size}
        className={className}
        showProgress={contentLoaderProps.showProgress ?? true}
      />
    </div>
  )
}

AppLoader.displayName = 'AppLoader'
